// Espace employé — HR document request (attestation / ATS /
// attestation de congé / fiche de paie / autre), two panes:
// configuration → recap, then confirmation. Adapted from the design's
// EsDocModal; deviations: the attestation de travail SCAN is
// self-service (direct PDF download), and fiche de paie months are
// simple client-side chips (year-to-date) — there is no payslip
// depository, the RH prepares the document on request.
import { useMemo, useState } from 'react';
import type { DocRequestForme, DocRequestType, MeLeaveRequestDTO, MeOverview } from '../../api/hooks.js';
import { useCreateMyDocRequest } from '../../api/hooks.js';
import { CG_MONTHS, cgFmtRange, cgToday } from '../conges/common.js';
import { DOC_SERVICE, DOC_SLA, ES_SERVICES, ES_TONE, EsTrack } from './common.js';
import { toast } from '../../store/toast.js';

const ES_DEST = ['Banque / organisme de crédit', 'Ambassade / visa', 'Bailleur / agence', 'Administration (CNAS, impôts…)', 'Usage personnel'];

export function EsDocModal({ overview, type, validatedLeaves, onClose, onSent }: {
  overview: MeOverview;
  type: DocRequestType;
  validatedLeaves: MeLeaveRequestDTO[];
  onClose: () => void;
  onSent: () => void;
}) {
  const isPaie = type === 'FICHE_PAIE';
  const isConge = type === 'ATTESTATION_CONGE';
  const isAutre = type === 'AUTRE';
  const svc = ES_SERVICES[DOC_SERVICE[type]];
  const [bg, fg] = ES_TONE[svc.tone];

  const today = cgToday();
  const year = Number(today.slice(0, 4));
  const months = useMemo(() => {
    // Jan → last closed month (previous month), newest first.
    const lastClosed = Number(today.slice(5, 7)) - 1;
    const out: Array<{ key: string; lbl: string }> = [];
    for (let m = 1; m <= lastClosed; m++) out.push({ key: `${year}-${String(m).padStart(2, '0')}`, lbl: CG_MONTHS[m - 1]! });
    return out.reverse();
  }, [today, year]);

  const [step, setStep] = useState(0);
  const [sel, setSel] = useState<string[]>(isPaie && months.length ? [months[0]!.key] : []);
  const [forme, setForme] = useState<DocRequestForme>(isPaie || type === 'ATTESTATION_TRAVAIL' ? 'SCAN' : 'ORIGINAL');
  const [dest, setDest] = useState(ES_DEST[0]!);
  const [lang, setLang] = useState<'fr' | 'ar' | 'bi'>('fr');
  const [copies, setCopies] = useState(1);
  const [period, setPeriod] = useState(3);
  const [note, setNote] = useState('');
  const [leaveId, setLeaveId] = useState<string | null>(isConge ? (validatedLeaves[0]?.id ?? null) : null);
  const [sent, setSent] = useState(false);
  const create = useCreateMyDocRequest();

  const leave = validatedLeaves.find((l) => l.id === leaveId) ?? null;
  const ok = isAutre ? note.trim().length > 3 : isPaie ? sel.length > 0 : isConge ? !!leave : true;
  const toggle = (k: string) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const last = (n: number) => setSel(months.slice(0, n).map((m) => m.key));
  const selLbl = [...sel].sort().map((k) => CG_MONTHS[Number(k.slice(-2)) - 1]).join(', ');

  const send = () => {
    create.mutate({
      type, forme, lang, copies,
      months: isPaie ? [...sel].sort() : [],
      leaveStart: isConge && leave ? leave.startDate : undefined,
      leaveEnd: isConge && leave ? leave.endDate : undefined,
      salaryMonths: type === 'ATS' ? period : undefined,
      dest: !isPaie && !isAutre ? dest : undefined,
      note: note.trim() || undefined,
    } as never, {
      onSuccess: () => { setSent(true); setTimeout(onSent, 1500); },
      onError: () => toast.danger("La demande n'a pas pu être envoyée."),
    });
  };

  return (
    <div className="cg-veil center" onClick={onClose}>
      <div className="es-modal" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div className="es-sent">
            <span className="es-check">✓</span>
            <b>Demande enregistrée</b>
            <span className="es-sent-sub">
              {forme === 'ORIGINAL'
                ? 'La RH prépare le document puis le transmet au DG pour signature. Vous serez notifié dès qu’il est prêt à retirer au siège.'
                : 'La RH prépare votre document signé et vous le transmet par email.'}
            </span>
            <span className="es-sent-rec gm-mono">{svc.lbl} · {forme === 'ORIGINAL' ? 'original signé DG' : 'copie électronique'} · {DOC_SLA[forme]}</span>
          </div>
        ) : (<>
          <div className="es-mo-head">
            <span className="es-mo-ic" style={{ background: bg, color: fg }}>{svc.ic}</span>
            <div>
              <div className="sb-mo-title">{svc.lbl}</div>
              <div className="sb-mo-sub">{overview.employee.name} · {overview.employee.entity.short} · {overview.employee.department ?? overview.employee.code}</div>
            </div>
            <button className="sb-dr-x" onClick={onClose}>✕</button>
          </div>

          {step === 0 ? (
            <div className="es-pane slideL">
              {isPaie && (
                <div className="es-fld">
                  <div className="es-fld-h">
                    <span className="es-fld-l">Mois demandés</span>
                    <span className="es-presets">
                      <button onClick={() => last(3)}>3 derniers</button>
                      <button onClick={() => last(6)}>6 derniers</button>
                      <button onClick={() => setSel(months.map((m) => m.key))}>Tout {year}</button>
                      <button onClick={() => setSel([])}>Vider</button>
                    </span>
                  </div>
                  <div className="es-monthgrid">
                    {months.map((m) => (
                      <button key={m.key} className={'es-mchip' + (sel.includes(m.key) ? ' on' : '')} onClick={() => toggle(m.key)}>
                        <b>{m.lbl}</b><i>{m.key}</i>
                        {sel.includes(m.key) && <em>✓</em>}
                      </button>
                    ))}
                    {months.length === 0 && <div className="cg-empty sm">Aucun mois de paie clôturé cette année.</div>}
                  </div>
                  <div className="es-fld-hint">{sel.length ? `${sel.length} mois sélectionné${sel.length > 1 ? 's' : ''} · ${selLbl}` : 'Sélectionnez un ou plusieurs mois.'}</div>
                </div>
              )}

              {isConge && (
                <div className="es-fld">
                  <span className="es-fld-l">Congé concerné</span>
                  {validatedLeaves.length === 0
                    ? <div className="cg-empty">Aucun congé validé à attester pour le moment.</div>
                    : (
                      <div className="es-leavelist">
                        {validatedLeaves.slice(0, 8).map((l) => (
                          <button key={l.id} className={'es-lvchip' + (leaveId === l.id ? ' on' : '')} onClick={() => setLeaveId(l.id)}>
                            <span className="es-lvdot" style={{ background: l.type === 'RECUP' ? 'var(--infoc)' : 'var(--shf)' }} />
                            <span className="es-lvm">
                              <b>{cgFmtRange(l.startDate, l.endDate)}</b>
                              <i>{l.type === 'RECUP' ? 'Récupération' : 'Congé annuel'} · {l.daysCount} j ouvrable{l.daysCount > 1 ? 's' : ''}</i>
                            </span>
                            {leaveId === l.id && <em>✓</em>}
                          </button>
                        ))}
                      </div>
                    )}
                  <div className="es-fld-hint">L’attestation mentionne les dates, le nombre de jours et la validation de votre responsable.</div>
                </div>
              )}

              {type === 'ATS' && (
                <div className="es-fld">
                  <span className="es-fld-l">Période de salaire mentionnée</span>
                  <div className="cg-hgrid three">
                    {[3, 6, 12].map((n) => (
                      <button key={n} className={'cg-hbtn' + (period === n ? ' on' : '')} onClick={() => setPeriod(n)}>
                        {n} derniers mois<i>Salaire brut & net moyen</i>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {isAutre && (
                <div className="es-fld">
                  <span className="es-fld-l">Quel document vous faut-il ?</span>
                  <textarea className="es-ta" rows={3} placeholder="Ex. attestation de domiciliation bancaire, certificat de stage, attestation de reprise…" value={note} onChange={(e) => setNote(e.target.value)} />
                </div>
              )}

              <div className="es-fld">
                <span className="es-fld-l">Forme du document</span>
                <div className="cg-hgrid two">
                  <button className={'cg-hbtn' + (forme === 'SCAN' ? ' on' : '')} onClick={() => setForme('SCAN')}>
                    Copie électronique<i>Signature numérique RH · {DOC_SLA.SCAN}</i>
                  </button>
                  <button className={'cg-hbtn' + (forme === 'ORIGINAL' ? ' on' : '')} onClick={() => setForme('ORIGINAL')}>
                    Original signé par le DG<i>Cachet + signature manuscrite · {DOC_SLA.ORIGINAL}</i>
                  </button>
                </div>
                <div className="es-fld-hint">
                  {forme === 'ORIGINAL'
                    ? 'À retirer au siège auprès de la RH, ou remis par votre responsable.'
                    : 'Suffisant dans la majorité des cas ; demandez l’original si l’organisme l’exige.'}
                </div>
              </div>

              {!isPaie && !isAutre && (
                <div className="es-fld two">
                  <div>
                    <span className="es-fld-l">Destinataire</span>
                    <select className="es-sel" value={dest} onChange={(e) => setDest(e.target.value)}>
                      {ES_DEST.map((d) => <option key={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <span className="es-fld-l">Langue</span>
                    <div className="es-seg">
                      {([['fr', 'Français'], ['ar', 'العربية'], ['bi', 'Bilingue']] as const).map(([k, l]) => (
                        <button key={k} className={lang === k ? 'on' : ''} onClick={() => setLang(k)}>{l}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="es-fld two">
                <div>
                  <span className="es-fld-l">Nombre d’exemplaires</span>
                  <div className="es-step2">
                    <button onClick={() => setCopies((c) => Math.max(1, c - 1))}>−</button>
                    <b className="gm-mono">{copies}</b>
                    <button onClick={() => setCopies((c) => Math.min(5, c + 1))}>＋</button>
                  </div>
                </div>
                {!isAutre && (
                  <div>
                    <span className="es-fld-l">Précision pour la RH (facultatif)</span>
                    <input className="es-in" placeholder="Nom de l’organisme, mention à ajouter…" value={note} onChange={(e) => setNote(e.target.value)} />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="es-pane slideL">
              <div className="es-recap">
                <div className="es-recap-h" style={{ background: bg, color: fg }}>{svc.lbl}</div>
                {isPaie && <div className="es-recap-row"><span>Mois</span><b>{selLbl}</b></div>}
                {isConge && leave && <div className="es-recap-row"><span>Congé attesté</span><b>{cgFmtRange(leave.startDate, leave.endDate)} · {leave.daysCount} j</b></div>}
                {type === 'ATS' && <div className="es-recap-row"><span>Période de salaire</span><b>{period} derniers mois</b></div>}
                {isAutre && <div className="es-recap-row"><span>Objet</span><b>{note}</b></div>}
                <div className="es-recap-row"><span>Forme</span><b>{forme === 'ORIGINAL' ? 'Original signé par le DG' : 'Copie électronique signée'}</b></div>
                {!isPaie && !isAutre && <div className="es-recap-row"><span>Destinataire · langue</span><b>{dest} · {lang === 'fr' ? 'Français' : lang === 'ar' ? 'Arabe' : 'Bilingue'}</b></div>}
                <div className="es-recap-row"><span>Exemplaires</span><b>{copies}</b></div>
                <div className="es-recap-row"><span>Délai annoncé</span><b>{DOC_SLA[forme]}</b></div>
                <div className="es-recap-row"><span>Traitée par</span><b>La RH{forme === 'ORIGINAL' ? ' → signature DG' : ''}</b></div>
              </div>
              <div className="es-trackwrap">
                <span className="es-fld-l">Parcours de la demande</span>
                <EsTrack status="RECUE" forme={forme} />
              </div>
            </div>
          )}

          <div className="cg-mo-foot">
            {step > 0 && <button className="cg-ghost" onClick={() => setStep(0)}>‹ Modifier</button>}
            <button className="cg-ghost" onClick={onClose}>Annuler</button>
            {step === 0
              ? <button className="gm-attn-go" disabled={!ok} onClick={() => setStep(1)}>Vérifier{isPaie && sel.length ? ` · ${sel.length} mois` : ''}</button>
              : <button className="gm-attn-go" disabled={create.isPending} onClick={send}>Envoyer la demande</button>}
          </div>
        </>)}
      </div>
    </div>
  );
}
